/**
 * LandingPageNew - Redesigned Landing Page for Cenari
 * 
 * THEME: Always uses Dynamics theme (light, MS Fluent Design)
 * RESPONSIVE: Uses rem/vw units for zoom/resolution independence
 * GRAPHICS: SVG-based interactive demos matching actual UI
 * WCAG: All colors meet AA contrast requirements (4.5:1 text, 3:1 large/UI)
 * DATA: Fictional companies, contacts, and locations only
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  Map, Users, FileText, ChevronRight, CheckCircle2,
  CalendarDays, Menu, X, Building2, Route, ClipboardCheck,
  Mail, Tag, Clock, BarChart3, FolderKanban, Target,
  Sparkles, ArrowRight, Play, MapPin, Calendar, Bell,
  TrendingUp, Zap, Shield, Globe, Phone, User, Search,
  Filter, Download, Settings, ChevronDown, Eye, Edit,
  Trash2, Plus, MoreHorizontal, Folder, Star, AlertCircle,
  CheckSquare, Square, Circle, Hash, Printer
} from 'lucide-react';
import { ROUTES } from '@app/routes';

// ============================================================
// WCAG AA COMPLIANT COLORS (Dynamics Theme)
// All text colors have 4.5:1+ contrast against their backgrounds
// All UI elements have 3:1+ contrast
// ============================================================
const COLORS = {
  // Backgrounds
  bgApp: '#f3f2f1',           // Main page background
  bgPanel: '#ffffff',          // Cards/panels
  bgHeader: '#ffffff',         // Header
  bgHover: '#e1dfdd',          // Hover state (darker for visibility)
  bgAccent: '#0078d4',         // Microsoft blue (primary accent)
  bgAccentHover: '#106ebe',    // Accent hover
  bgAccentLight: '#cfe4fa',    // Light accent (darkened for contrast)
  
  // Borders - WCAG 3:1 against backgrounds
  border: '#8a8886',           // Default border (5.0:1 on white)
  borderLight: '#c8c6c4',      // Subtle border (3.2:1 on white)
  borderAccent: '#0078d4',     // Accent border
  
  // Text - WCAG AA compliant (4.5:1 minimum)
  textPrimary: '#201f1e',      // Primary text (12.6:1 on white)
  textSecondary: '#484644',    // Secondary text (7.0:1 on white)
  textMuted: '#605e5c',        // Muted text (4.6:1 on white)
  textAccent: '#0062a3',       // Accent text (5.9:1 on white, darker than bgAccent)
  textOnAccent: '#ffffff',     // Text on accent backgrounds (4.5:1 on #0078d4)
  textOnDark: '#ffffff',       // Text on dark backgrounds
  
  // Status colors - WCAG compliant
  success: '#0b6a0b',          // Success green (7.5:1 on white)
  successLight: '#dff6dd',     // Success bg
  successText: '#0b6a0b',      // Text on success light bg (7.5:1)
  warning: '#8a4b00',          // Warning orange (5.8:1 on white)
  warningLight: '#fff4ce',     // Warning bg
  warningText: '#6b3a00',      // Text on warning bg (6.4:1)
  error: '#c42b1c',            // Error red (5.2:1 on white)
  errorLight: '#fde7e9',       // Error bg
  errorText: '#a4262c',        // Text on error bg (5.9:1)
  
  // Feature colors (all 4.5:1+ on white)
  purple: '#5c2d91',           // 7.8:1 on white
  purpleLight: '#e8dcf4',
  green: '#0b6a0b',            // 7.5:1 on white
  greenLight: '#dff6dd',
  orange: '#8a4b00',           // 5.8:1 on white
  orangeLight: '#fff4ce',
  pink: '#80003e',             // 8.2:1 on white
  pinkLight: '#f6e3ed',
  blue: '#0062a3',             // 5.9:1 on white
  blueLight: '#cfe4fa',
  teal: '#006666',             // 6.5:1 on white
  tealLight: '#d4f4f4',
} as const;

// ============================================================
// FICTIONAL DATA (No real companies, no USI, no Houston)
// ============================================================
const FICTIONAL = {
  companies: [
    'Pinnacle Manufacturing',
    'Summit Healthcare Group',
    'Cascade Financial',
    'Metro Industries',
    'Horizon Logistics',
    'Atlas Engineering',
    'Sterling Hospitality',
    'Nexus Technology',
    'Crestview Properties',
    'Beacon Services',
  ],
  contacts: [
    'Sarah Mitchell',
    'James Chen',
    'Maria Rodriguez',
    'David Thompson',
    'Jennifer Walsh',
    'Michael Brooks',
    'Amanda Foster',
    'Robert Kim',
  ],
  cities: [
    'Springfield',
    'Riverside',
    'Lakewood',
    'Fairview',
    'Oakdale',
    'Millbrook',
    'Greenfield',
    'Westgate',
  ],
  states: ['IL', 'CA', 'TX', 'FL', 'NY', 'OH', 'PA', 'GA'],
};

// ============================================================
// RESPONSIVE STYLES
// ============================================================
const styles = {
  container: {
    maxWidth: '75rem',
    margin: '0 auto',
    padding: '0 clamp(1rem, 4vw, 3rem)',
  } as React.CSSProperties,
  
  h1: {
    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
    fontWeight: 700,
    lineHeight: 1.1,
    color: COLORS.textPrimary,
  } as React.CSSProperties,
  
  h2: {
    fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
    fontWeight: 700,
    lineHeight: 1.2,
    color: COLORS.textPrimary,
  } as React.CSSProperties,
  
  h3: {
    fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
    fontWeight: 600,
    lineHeight: 1.3,
    color: COLORS.textPrimary,
  } as React.CSSProperties,
  
  body: {
    fontSize: 'clamp(0.9375rem, 1.5vw, 1.125rem)',
    lineHeight: 1.6,
    color: COLORS.textSecondary,
  } as React.CSSProperties,
  
  small: {
    fontSize: 'clamp(0.8125rem, 1.2vw, 0.875rem)',
    lineHeight: 1.5,
    color: COLORS.textMuted,
  } as React.CSSProperties,
};

// ============================================================
// SCROLL REVEAL
// ============================================================
const ScrollReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] } }
      }}
    >
      {children}
    </motion.div>
  );
};

// ============================================================
// DEMO: Campaign Management Table
// ============================================================
const CampaignTableDemo: React.FC = () => {
  const campaigns = [
    { name: 'Q1 Manufacturing Outreach', orgs: 147, status: 'Active', progress: 68 },
    { name: 'Healthcare Spring Push', orgs: 89, status: 'Active', progress: 42 },
    { name: 'Financial Services Drop', orgs: 156, status: 'Planning', progress: 0 },
    { name: 'Retail Benefits Blitz', orgs: 203, status: 'Completed', progress: 100 },
  ];
  
  return (
    <div style={{
      backgroundColor: COLORS.bgPanel,
      borderRadius: '0.5rem',
      border: `1px solid ${COLORS.borderLight}`,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '0.75rem 1rem',
        borderBottom: `1px solid ${COLORS.borderLight}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: COLORS.bgApp,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FolderKanban size={16} color={COLORS.textAccent} />
          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: COLORS.textPrimary }}>Campaigns</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <div style={{
            padding: '0.25rem 0.5rem',
            borderRadius: '0.25rem',
            border: `1px solid ${COLORS.borderLight}`,
            fontSize: '0.6875rem',
            color: COLORS.textSecondary,
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
          }}>
            <Filter size={10} />
            Filter
          </div>
          <div style={{
            padding: '0.25rem 0.5rem',
            borderRadius: '0.25rem',
            backgroundColor: COLORS.bgAccent,
            color: COLORS.textOnAccent,
            fontSize: '0.6875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
          }}>
            <Plus size={10} />
            New
          </div>
        </div>
      </div>
      
      {/* Table */}
      <div style={{ fontSize: '0.75rem' }}>
        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
          padding: '0.5rem 1rem',
          borderBottom: `1px solid ${COLORS.borderLight}`,
          backgroundColor: COLORS.bgApp,
          fontWeight: 600,
          color: COLORS.textSecondary,
        }}>
          <span>Campaign Name</span>
          <span>Organizations</span>
          <span>Status</span>
          <span>Progress</span>
        </div>
        
        {/* Table rows */}
        {campaigns.map((campaign, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.1 + 0.2 }}
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
              padding: '0.625rem 1rem',
              borderBottom: i < campaigns.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none',
              alignItems: 'center',
            }}
          >
            <span style={{ color: COLORS.textPrimary, fontWeight: 500 }}>{campaign.name}</span>
            <span style={{ color: COLORS.textSecondary }}>{campaign.orgs}</span>
            <span style={{
              display: 'inline-flex',
              padding: '0.125rem 0.375rem',
              borderRadius: '0.25rem',
              fontSize: '0.625rem',
              fontWeight: 600,
              backgroundColor: campaign.status === 'Active' ? COLORS.greenLight : 
                              campaign.status === 'Completed' ? COLORS.blueLight : COLORS.orangeLight,
              color: campaign.status === 'Active' ? COLORS.successText : 
                     campaign.status === 'Completed' ? COLORS.blue : COLORS.warningText,
            }}>
              {campaign.status}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                flex: 1,
                height: '0.375rem',
                backgroundColor: COLORS.bgApp,
                borderRadius: '0.1875rem',
                overflow: 'hidden',
              }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${campaign.progress}%` }}
                  transition={{ delay: i * 0.1 + 0.3, duration: 0.5 }}
                  style={{
                    height: '100%',
                    backgroundColor: campaign.progress === 100 ? COLORS.success : COLORS.bgAccent,
                    borderRadius: '0.1875rem',
                  }}
                />
              </div>
              <span style={{ color: COLORS.textSecondary, minWidth: '2rem' }}>{campaign.progress}%</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// DEMO: Organization List
// ============================================================
const OrganizationListDemo: React.FC = () => {
  const orgs = [
    { name: FICTIONAL.companies[0], city: FICTIONAL.cities[0], state: FICTIONAL.states[0], employees: 450, status: 'Prospect' },
    { name: FICTIONAL.companies[1], city: FICTIONAL.cities[1], state: FICTIONAL.states[1], employees: 1200, status: 'Client' },
    { name: FICTIONAL.companies[2], city: FICTIONAL.cities[2], state: FICTIONAL.states[2], employees: 320, status: 'Lead' },
    { name: FICTIONAL.companies[3], city: FICTIONAL.cities[3], state: FICTIONAL.states[3], employees: 890, status: 'Prospect' },
  ];
  
  return (
    <div style={{
      backgroundColor: COLORS.bgPanel,
      borderRadius: '0.5rem',
      border: `1px solid ${COLORS.borderLight}`,
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '0.75rem 1rem',
        borderBottom: `1px solid ${COLORS.borderLight}`,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        backgroundColor: COLORS.bgApp,
      }}>
        <Building2 size={16} color={COLORS.textAccent} />
        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: COLORS.textPrimary }}>Organizations</span>
        <span style={{
          marginLeft: 'auto',
          fontSize: '0.6875rem',
          color: COLORS.textMuted,
          backgroundColor: COLORS.bgHover,
          padding: '0.125rem 0.5rem',
          borderRadius: '1rem',
        }}>
          847 total
        </span>
      </div>
      
      {orgs.map((org, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1 + 0.2 }}
          style={{
            padding: '0.75rem 1rem',
            borderBottom: i < orgs.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <div style={{
            width: '2rem',
            height: '2rem',
            borderRadius: '0.25rem',
            backgroundColor: COLORS.blueLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Building2 size={14} color={COLORS.blue} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, fontSize: '0.8125rem', color: COLORS.textPrimary }}>{org.name}</div>
            <div style={{ fontSize: '0.6875rem', color: COLORS.textMuted }}>
              {org.city}, {org.state} • {org.employees} employees
            </div>
          </div>
          <span style={{
            fontSize: '0.625rem',
            fontWeight: 600,
            padding: '0.125rem 0.375rem',
            borderRadius: '0.25rem',
            backgroundColor: org.status === 'Client' ? COLORS.greenLight : 
                            org.status === 'Lead' ? COLORS.orangeLight : COLORS.blueLight,
            color: org.status === 'Client' ? COLORS.successText : 
                   org.status === 'Lead' ? COLORS.warningText : COLORS.blue,
          }}>
            {org.status}
          </span>
        </motion.div>
      ))}
    </div>
  );
};

// ============================================================
// DEMO: Route Optimization Map
// ============================================================
const RouteMapDemo: React.FC = () => {
  const [activeStop, setActiveStop] = useState(0);
  
  const stops = [
    { x: 15, y: 25, name: FICTIONAL.companies[0].split(' ')[0] },
    { x: 35, y: 50, name: FICTIONAL.companies[1].split(' ')[0] },
    { x: 60, y: 30, name: FICTIONAL.companies[2].split(' ')[0] },
    { x: 85, y: 55, name: FICTIONAL.companies[3].split(' ')[0] },
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStop(prev => (prev + 1) % stops.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div style={{
      backgroundColor: COLORS.bgPanel,
      borderRadius: '0.5rem',
      border: `1px solid ${COLORS.borderLight}`,
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '0.75rem 1rem',
        borderBottom: `1px solid ${COLORS.borderLight}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: COLORS.bgApp,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Route size={16} color={COLORS.green} />
          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: COLORS.textPrimary }}>Week 1 Route</span>
        </div>
        <div style={{ fontSize: '0.6875rem', color: COLORS.textMuted }}>4 stops • 2.3 hrs drive time</div>
      </div>
      
      <svg viewBox="0 0 100 70" style={{ width: '100%', height: 'auto', display: 'block' }}>
        {/* Background */}
        <rect width="100" height="70" fill={COLORS.bgApp} />
        
        {/* Grid pattern */}
        <defs>
          <pattern id="routeGrid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke={COLORS.borderLight} strokeWidth="0.3" />
          </pattern>
        </defs>
        <rect width="100" height="70" fill="url(#routeGrid)" />
        
        {/* Route path */}
        <motion.path
          d={`M ${stops.map(s => `${s.x} ${s.y}`).join(' L ')}`}
          fill="none"
          stroke={COLORS.green}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="4 2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />
        
        {/* Stop markers */}
        {stops.map((stop, i) => (
          <g key={i}>
            <motion.circle
              cx={stop.x}
              cy={stop.y}
              r={activeStop === i ? 5 : 4}
              fill={activeStop === i ? COLORS.green : COLORS.bgPanel}
              stroke={COLORS.green}
              strokeWidth="2"
              animate={{ r: activeStop === i ? 5 : 4 }}
              transition={{ duration: 0.3 }}
            />
            <text
              x={stop.x}
              y={stop.y - 8}
              textAnchor="middle"
              fontSize="4"
              fontWeight={activeStop === i ? 600 : 400}
              fill={COLORS.textPrimary}
            >
              {stop.name}
            </text>
            <text
              x={stop.x}
              y={stop.y + 2}
              textAnchor="middle"
              fontSize="3.5"
              fill={activeStop === i ? COLORS.textOnAccent : COLORS.green}
              fontWeight="700"
            >
              {i + 1}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

// ============================================================
// DEMO: Execution Tracking
// ============================================================
const ExecutionTrackingDemo: React.FC = () => {
  const visits = [
    { company: FICTIONAL.companies[0], time: '9:00 AM', status: 'completed' },
    { company: FICTIONAL.companies[1], time: '10:30 AM', status: 'completed' },
    { company: FICTIONAL.companies[2], time: '1:00 PM', status: 'current' },
    { company: FICTIONAL.companies[3], time: '2:30 PM', status: 'pending' },
    { company: FICTIONAL.companies[4], time: '4:00 PM', status: 'pending' },
  ];
  
  return (
    <div style={{
      backgroundColor: COLORS.bgPanel,
      borderRadius: '0.5rem',
      border: `1px solid ${COLORS.borderLight}`,
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '0.75rem 1rem',
        borderBottom: `1px solid ${COLORS.borderLight}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: COLORS.bgApp,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ClipboardCheck size={16} color={COLORS.orange} />
          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: COLORS.textPrimary }}>Today's Visits</span>
        </div>
        <span style={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          padding: '0.125rem 0.5rem',
          borderRadius: '1rem',
          backgroundColor: COLORS.greenLight,
          color: COLORS.successText,
        }}>
          2/5 Complete
        </span>
      </div>
      
      {visits.map((visit, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: i * 0.1 + 0.2 }}
          style={{
            padding: '0.625rem 1rem',
            borderBottom: i < visits.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            backgroundColor: visit.status === 'current' ? COLORS.blueLight : 'transparent',
          }}
        >
          <div style={{
            width: '1.25rem',
            height: '1.25rem',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: visit.status === 'completed' ? COLORS.success : 
                            visit.status === 'current' ? COLORS.bgAccent : COLORS.bgApp,
            border: visit.status === 'pending' ? `2px solid ${COLORS.borderLight}` : 'none',
          }}>
            {visit.status === 'completed' && <CheckCircle2 size={12} color={COLORS.textOnAccent} />}
            {visit.status === 'current' && <Circle size={8} fill={COLORS.textOnAccent} color={COLORS.textOnAccent} />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontWeight: 500, 
              fontSize: '0.8125rem', 
              color: visit.status === 'pending' ? COLORS.textMuted : COLORS.textPrimary 
            }}>
              {visit.company}
            </div>
          </div>
          <span style={{ 
            fontSize: '0.6875rem', 
            color: visit.status === 'pending' ? COLORS.textMuted : COLORS.textSecondary 
          }}>
            {visit.time}
          </span>
        </motion.div>
      ))}
    </div>
  );
};

// ============================================================
// DEMO: Event Planning Grid
// ============================================================
const EPGDemo: React.FC = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const companies = [
    FICTIONAL.companies[0].split(' ')[0],
    FICTIONAL.companies[1].split(' ')[0],
    FICTIONAL.companies[2].split(' ')[0],
    FICTIONAL.companies[3].split(' ')[0],
  ];
  
  const events = [
    { company: 0, month: 0, type: 'fair', label: 'BF' },
    { company: 0, month: 3, type: 'enrollment', label: 'OE' },
    { company: 1, month: 1, type: 'meeting', label: 'M' },
    { company: 1, month: 4, type: 'fair', label: 'BF' },
    { company: 2, month: 2, type: 'followup', label: 'FU' },
    { company: 2, month: 5, type: 'enrollment', label: 'OE' },
    { company: 3, month: 0, type: 'meeting', label: 'M' },
    { company: 3, month: 3, type: 'fair', label: 'BF' },
  ];
  
  const eventColors: Record<string, { bg: string; text: string }> = {
    fair: { bg: COLORS.greenLight, text: COLORS.successText },
    enrollment: { bg: COLORS.blueLight, text: COLORS.blue },
    meeting: { bg: COLORS.purpleLight, text: COLORS.purple },
    followup: { bg: COLORS.orangeLight, text: COLORS.warningText },
  };
  
  return (
    <div style={{
      backgroundColor: COLORS.bgPanel,
      borderRadius: '0.5rem',
      border: `1px solid ${COLORS.borderLight}`,
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '0.75rem 1rem',
        borderBottom: `1px solid ${COLORS.borderLight}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: COLORS.bgApp,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <CalendarDays size={16} color={COLORS.purple} />
          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: COLORS.textPrimary }}>Event Planning Grid</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[
            { label: 'BF', name: 'Benefit Fair', color: eventColors.fair },
            { label: 'OE', name: 'Open Enrollment', color: eventColors.enrollment },
            { label: 'M', name: 'Meeting', color: eventColors.meeting },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.5625rem' }}>
              <span style={{
                padding: '0.0625rem 0.25rem',
                borderRadius: '0.125rem',
                backgroundColor: item.color.bg,
                color: item.color.text,
                fontWeight: 600,
              }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      
      <div style={{ padding: '0.5rem', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.6875rem' }}>
          <thead>
            <tr>
              <th style={{ 
                padding: '0.375rem', 
                textAlign: 'left', 
                fontWeight: 600, 
                color: COLORS.textSecondary,
                borderBottom: `1px solid ${COLORS.borderLight}`,
                minWidth: '5rem',
              }}>
                Organization
              </th>
              {months.map(month => (
                <th key={month} style={{ 
                  padding: '0.375rem', 
                  textAlign: 'center', 
                  fontWeight: 600, 
                  color: COLORS.textSecondary,
                  borderBottom: `1px solid ${COLORS.borderLight}`,
                  minWidth: '2.5rem',
                }}>
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {companies.map((company, rowIdx) => (
              <tr key={rowIdx}>
                <td style={{ 
                  padding: '0.5rem 0.375rem', 
                  fontWeight: 500, 
                  color: COLORS.textPrimary,
                  borderBottom: `1px solid ${COLORS.borderLight}`,
                }}>
                  {company}
                </td>
                {months.map((_, colIdx) => {
                  const event = events.find(e => e.company === rowIdx && e.month === colIdx);
                  return (
                    <td key={colIdx} style={{ 
                      padding: '0.25rem', 
                      textAlign: 'center',
                      borderBottom: `1px solid ${COLORS.borderLight}`,
                      backgroundColor: rowIdx % 2 === 0 ? 'transparent' : COLORS.bgApp,
                    }}>
                      {event && (
                        <motion.span
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: (rowIdx * 6 + colIdx) * 0.02 + 0.3 }}
                          style={{
                            display: 'inline-block',
                            padding: '0.125rem 0.375rem',
                            borderRadius: '0.1875rem',
                            backgroundColor: eventColors[event.type].bg,
                            color: eventColors[event.type].text,
                            fontWeight: 600,
                            fontSize: '0.5625rem',
                          }}
                        >
                          {event.label}
                        </motion.span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
// DEMO: Follow-ups List
// ============================================================
const FollowUpsDemo: React.FC = () => {
  const followups = [
    { company: FICTIONAL.companies[0], contact: FICTIONAL.contacts[0], type: 'Call back', due: 'Today', priority: 'high' },
    { company: FICTIONAL.companies[1], contact: FICTIONAL.contacts[1], type: 'Send quote', due: 'Tomorrow', priority: 'medium' },
    { company: FICTIONAL.companies[2], contact: FICTIONAL.contacts[2], type: 'Schedule meeting', due: 'This week', priority: 'low' },
  ];
  
  return (
    <div style={{
      backgroundColor: COLORS.bgPanel,
      borderRadius: '0.5rem',
      border: `1px solid ${COLORS.borderLight}`,
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '0.75rem 1rem',
        borderBottom: `1px solid ${COLORS.borderLight}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: COLORS.bgApp,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Bell size={16} color={COLORS.orange} />
          <span style={{ fontWeight: 600, fontSize: '0.875rem', color: COLORS.textPrimary }}>Follow-ups Due</span>
        </div>
        <span style={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          padding: '0.125rem 0.5rem',
          borderRadius: '1rem',
          backgroundColor: COLORS.errorLight,
          color: COLORS.errorText,
        }}>
          3 pending
        </span>
      </div>
      
      {followups.map((fu, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: i * 0.15 + 0.2 }}
          style={{
            padding: '0.75rem 1rem',
            borderBottom: i < followups.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <div style={{
            width: '0.5rem',
            height: '0.5rem',
            borderRadius: '50%',
            backgroundColor: fu.priority === 'high' ? COLORS.error : 
                            fu.priority === 'medium' ? COLORS.warning : COLORS.success,
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, fontSize: '0.8125rem', color: COLORS.textPrimary }}>{fu.type}</div>
            <div style={{ fontSize: '0.6875rem', color: COLORS.textMuted }}>
              {fu.company} • {fu.contact}
            </div>
          </div>
          <span style={{ 
            fontSize: '0.6875rem', 
            fontWeight: 500,
            color: fu.due === 'Today' ? COLORS.errorText : COLORS.textSecondary,
          }}>
            {fu.due}
          </span>
        </motion.div>
      ))}
    </div>
  );
};

// ============================================================
// DEMO: Label Generator Preview
// ============================================================
const LabelGeneratorDemo: React.FC = () => (
  <div style={{
    backgroundColor: COLORS.bgPanel,
    borderRadius: '0.5rem',
    border: `1px solid ${COLORS.borderLight}`,
    overflow: 'hidden',
  }}>
    <div style={{
      padding: '0.75rem 1rem',
      borderBottom: `1px solid ${COLORS.borderLight}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: COLORS.bgApp,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Tag size={16} color={COLORS.pink} />
        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: COLORS.textPrimary }}>Label Generator</span>
      </div>
      <div style={{
        padding: '0.25rem 0.5rem',
        borderRadius: '0.25rem',
        backgroundColor: COLORS.bgAccent,
        color: COLORS.textOnAccent,
        fontSize: '0.6875rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
      }}>
        <Printer size={10} />
        Print Labels
      </div>
    </div>
    
    <div style={{ padding: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
      {[
        { name: FICTIONAL.companies[0], contact: FICTIONAL.contacts[0], city: FICTIONAL.cities[0], state: FICTIONAL.states[0] },
        { name: FICTIONAL.companies[1], contact: FICTIONAL.contacts[1], city: FICTIONAL.cities[1], state: FICTIONAL.states[1] },
      ].map((label, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.15 + 0.2 }}
          style={{
            flex: '1 1 45%',
            minWidth: '8rem',
            padding: '0.625rem',
            border: `1px dashed ${COLORS.border}`,
            borderRadius: '0.25rem',
            backgroundColor: COLORS.bgApp,
          }}
        >
          <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: COLORS.textPrimary }}>{label.contact}</div>
          <div style={{ fontSize: '0.625rem', color: COLORS.textSecondary }}>{label.name}</div>
          <div style={{ fontSize: '0.625rem', color: COLORS.textSecondary }}>123 Business Ave</div>
          <div style={{ fontSize: '0.625rem', color: COLORS.textSecondary }}>{label.city}, {label.state} 12345</div>
        </motion.div>
      ))}
    </div>
  </div>
);

// ============================================================
// DEMO: Dashboard Stats
// ============================================================
const DashboardStatsDemo: React.FC = () => (
  <div style={{
    backgroundColor: COLORS.bgPanel,
    borderRadius: '0.5rem',
    border: `1px solid ${COLORS.borderLight}`,
    overflow: 'hidden',
  }}>
    <div style={{
      padding: '0.75rem 1rem',
      borderBottom: `1px solid ${COLORS.borderLight}`,
      backgroundColor: COLORS.bgApp,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <BarChart3 size={16} color={COLORS.textAccent} />
        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: COLORS.textPrimary }}>Weekly Overview</span>
      </div>
    </div>
    
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
        {[
          { label: 'Visits', value: '47', change: '+12%', color: COLORS.success },
          { label: 'Callbacks', value: '18', change: '+5%', color: COLORS.blue },
          { label: 'Quotes Sent', value: '9', change: '+3', color: COLORS.purple },
        ].map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 + 0.2 }}
            style={{
              flex: 1,
              padding: '0.75rem',
              backgroundColor: COLORS.bgApp,
              borderRadius: '0.375rem',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '0.625rem', color: COLORS.textMuted }}>{stat.label}</div>
            <div style={{ fontSize: '0.5625rem', color: COLORS.success, fontWeight: 500 }}>{stat.change}</div>
          </motion.div>
        ))}
      </div>
      
      {/* Mini bar chart */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.375rem', height: '3rem' }}>
        {[40, 55, 35, 70, 50, 45, 60].map((height, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            animate={{ height: `${height}%` }}
            transition={{ delay: i * 0.05 + 0.4, duration: 0.3 }}
            style={{
              flex: 1,
              backgroundColor: i === 3 ? COLORS.bgAccent : COLORS.blueLight,
              borderRadius: '0.125rem 0.125rem 0 0',
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
          <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.5rem', color: COLORS.textMuted }}>
            {day}
          </span>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// FEATURE CARD
// ============================================================
interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  demo: React.ReactNode;
  accentColor: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon: Icon, title, description, demo, accentColor }) => (
  <div style={{
    backgroundColor: COLORS.bgPanel,
    borderRadius: '0.75rem',
    border: `1px solid ${COLORS.borderLight}`,
    overflow: 'hidden',
  }}>
    <div style={{ padding: 'clamp(1rem, 2vw, 1.5rem)' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '0.75rem',
      }}>
        <div style={{
          width: '2.5rem',
          height: '2.5rem',
          borderRadius: '0.5rem',
          backgroundColor: `${accentColor}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={20} color={accentColor} />
        </div>
        <div>
          <h3 style={{ ...styles.h3, fontSize: '1rem', marginBottom: '0.125rem' }}>{title}</h3>
          <p style={{ ...styles.small, margin: 0 }}>{description}</p>
        </div>
      </div>
    </div>
    <div style={{ padding: '0 clamp(1rem, 2vw, 1.5rem) clamp(1rem, 2vw, 1.5rem)' }}>
      {demo}
    </div>
  </div>
);

// ============================================================
// WORKFLOW STEP
// ============================================================
const WorkflowStep: React.FC<{
  step: number;
  title: string;
  description: string;
  isLast?: boolean;
}> = ({ step, title, description, isLast }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{
        width: 'clamp(2rem, 3vw, 2.5rem)',
        height: 'clamp(2rem, 3vw, 2.5rem)',
        borderRadius: '50%',
        backgroundColor: COLORS.bgAccent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.textOnAccent,
        fontWeight: 700,
        fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
      }}>
        {step}
      </div>
      {!isLast && (
        <div style={{
          width: '2px',
          height: 'clamp(1.5rem, 3vw, 2rem)',
          backgroundColor: COLORS.blueLight,
          marginTop: '0.5rem',
        }} />
      )}
    </div>
    <div style={{ paddingTop: '0.25rem', flex: 1 }}>
      <h4 style={{ fontWeight: 600, color: COLORS.textPrimary, marginBottom: '0.125rem', fontSize: '0.9375rem' }}>
        {title}
      </h4>
      <p style={{ ...styles.small, margin: 0 }}>{description}</p>
    </div>
  </div>
);

// ============================================================
// MAIN COMPONENT
// ============================================================
export const LandingPageNew: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleGetStarted = () => navigate(ROUTES.LOGIN);
  const handlePricing = () => navigate(ROUTES.PRICING);

  const pageStyle: React.CSSProperties = {
    backgroundColor: COLORS.bgApp,
    color: COLORS.textPrimary,
    minHeight: '100vh',
    fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif',
    overflowX: 'hidden',
    overflowY: 'auto',
    position: 'relative',
  };

  return (
    <div style={pageStyle} className="public-scrollable-page">
      {/* HEADER */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        backgroundColor: `${COLORS.bgHeader}f8`,
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${COLORS.borderLight}`,
      }}>
        <div style={{
          ...styles.container,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 'clamp(3.5rem, 6vw, 4rem)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '0.375rem',
              backgroundColor: COLORS.bgAccent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Target size={16} color={COLORS.textOnAccent} />
            </div>
            <span style={{ fontSize: '1.25rem', fontWeight: 700, color: COLORS.textPrimary }}>Cenari</span>
          </div>

          <nav style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(1.5rem, 3vw, 2rem)',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}>
            {['Features', 'How It Works', 'Pricing'].map((item, i) => (
              <a
                key={i}
                href={i === 2 ? undefined : `#${item.toLowerCase().replace(/\s/g, '-')}`}
                onClick={i === 2 ? handlePricing : undefined}
                style={{
                  color: COLORS.textSecondary,
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                {item}
              </a>
            ))}
          </nav>

          <button
            onClick={handleGetStarted}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: COLORS.bgAccent,
              color: COLORS.textOnAccent,
              border: 'none',
              borderRadius: '0.375rem',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          >
            Start Free Trial
            <ChevronRight size={16} />
          </button>
        </div>
      </header>

      {/* HERO */}
      <section style={{ paddingTop: 'clamp(6rem, 12vw, 8rem)', paddingBottom: 'clamp(2rem, 4vw, 3rem)' }}>
        <div style={styles.container}>
          <div style={{ maxWidth: '48rem', margin: '0 auto', textAlign: 'center' }}>
            <ScrollReveal>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '2rem',
                backgroundColor: COLORS.blueLight,
                border: `1px solid ${COLORS.bgAccent}30`,
                color: COLORS.textAccent,
                fontSize: '0.875rem',
                fontWeight: 500,
                marginBottom: '1.5rem',
              }}>
                <Sparkles size={16} />
                Built for Benefits Sales Professionals
              </div>

              <h1 style={{ ...styles.h1, marginBottom: '1.5rem' }}>
                Your Benefits Sales{' '}
                <span style={{ color: COLORS.textAccent }}>Command Center</span>
              </h1>

              <p style={{ ...styles.body, maxWidth: '36rem', margin: '0 auto 2rem' }}>
                Plan campaigns, optimize routes, execute drops, and track results — 
                all from one unified platform built for benefits sales.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center', marginBottom: '2rem' }}>
                <button
                  onClick={handleGetStarted}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.875rem 1.75rem',
                    backgroundColor: COLORS.bgAccent,
                    color: COLORS.textOnAccent,
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                >
                  Start Free Trial
                  <ArrowRight size={18} />
                </button>
                <button
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.875rem 1.75rem',
                    backgroundColor: COLORS.bgPanel,
                    color: COLORS.textPrimary,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                >
                  <Play size={18} />
                  Watch Demo
                </button>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', justifyContent: 'center', fontSize: '0.875rem', color: COLORS.textMuted }}>
                {['Pipedrive Integration', '14-Day Free Trial', 'No Credit Card Required'].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <CheckCircle2 size={16} color={COLORS.success} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FEATURES WITH DEMOS */}
      <section id="features" style={{ padding: 'clamp(2rem, 4vw, 3rem) 0' }}>
        <div style={styles.container}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
              <h2 style={styles.h2}>
                Everything You Need to <span style={{ color: COLORS.textAccent }}>Close More Deals</span>
              </h2>
              <p style={{ ...styles.body, maxWidth: '36rem', margin: '1rem auto 0' }}>
                See exactly how each feature works with live previews
              </p>
            </div>
          </ScrollReveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 22rem), 1fr))', gap: '1.5rem' }}>
            <ScrollReveal delay={0.1}>
              <FeatureCard
                icon={FolderKanban}
                title="Campaign Management"
                description="Organize prospects into targeted campaigns"
                demo={<CampaignTableDemo />}
                accentColor={COLORS.blue}
              />
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <FeatureCard
                icon={Building2}
                title="Organization Database"
                description="Manage all your target companies in one place"
                demo={<OrganizationListDemo />}
                accentColor={COLORS.teal}
              />
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <FeatureCard
                icon={Route}
                title="Route Optimization"
                description="Plan efficient multi-week visit schedules"
                demo={<RouteMapDemo />}
                accentColor={COLORS.green}
              />
            </ScrollReveal>

            <ScrollReveal delay={0.25}>
              <FeatureCard
                icon={ClipboardCheck}
                title="Execution Tracking"
                description="Track visits in real-time from the field"
                demo={<ExecutionTrackingDemo />}
                accentColor={COLORS.orange}
              />
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <FeatureCard
                icon={CalendarDays}
                title="Event Planning Grid"
                description="12-month calendar for benefit fairs and meetings"
                demo={<EPGDemo />}
                accentColor={COLORS.purple}
              />
            </ScrollReveal>

            <ScrollReveal delay={0.35}>
              <FeatureCard
                icon={Bell}
                title="Follow-up Management"
                description="Never miss a callback or next step"
                demo={<FollowUpsDemo />}
                accentColor={COLORS.orange}
              />
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <FeatureCard
                icon={Tag}
                title="Label Generator"
                description="Print mailing labels for campaigns"
                demo={<LabelGeneratorDemo />}
                accentColor={COLORS.pink}
              />
            </ScrollReveal>

            <ScrollReveal delay={0.45}>
              <FeatureCard
                icon={BarChart3}
                title="Dashboard & Reports"
                description="Track progress and measure results"
                demo={<DashboardStatsDemo />}
                accentColor={COLORS.blue}
              />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section id="how-it-works" style={{ padding: 'clamp(3rem, 6vw, 5rem) 0', backgroundColor: COLORS.bgPanel }}>
        <div style={styles.container}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 18rem), 1fr))', gap: 'clamp(2rem, 5vw, 4rem)', alignItems: 'center' }}>
            <ScrollReveal>
              <div>
                <h2 style={{ ...styles.h2, marginBottom: '1rem' }}>From Prospect to Renewal</h2>
                <p style={{ ...styles.body, marginBottom: '2rem' }}>
                  A complete workflow that guides you through the entire sales cycle
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <WorkflowStep step={1} title="Create Campaign" description="Define your target market and goals" />
                  <WorkflowStep step={2} title="Select Organizations" description="Filter and choose your targets" />
                  <WorkflowStep step={3} title="Calculate Routes" description="Optimize multi-week schedules" />
                  <WorkflowStep step={4} title="Execute Drops" description="Track visits in real-time" />
                  <WorkflowStep step={5} title="Follow Up" description="Never miss a callback" isLast />
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <DashboardStatsDemo />
                <ExecutionTrackingDemo />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section style={{ padding: 'clamp(3rem, 6vw, 5rem) 0' }}>
        <div style={styles.container}>
          <ScrollReveal>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 10rem), 1fr))', gap: '1.5rem' }}>
              {[
                { value: '5+', label: 'Hours Saved', sublabel: 'Per Week' },
                { value: '92%', label: 'Faster Planning', sublabel: 'vs Spreadsheets' },
                { value: '1500+', label: 'Visits Tracked', sublabel: 'Per Month' },
                { value: '100%', label: 'CRM Sync', sublabel: 'Automatic' },
              ].map((stat, i) => (
                <div key={i} style={{
                  textAlign: 'center',
                  padding: 'clamp(1rem, 2vw, 1.5rem)',
                  borderRadius: '0.75rem',
                  backgroundColor: COLORS.bgPanel,
                  border: `1px solid ${COLORS.borderLight}`,
                }}>
                  <div style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, color: COLORS.textAccent, marginBottom: '0.25rem' }}>
                    {stat.value}
                  </div>
                  <div style={{ fontWeight: 600, color: COLORS.textPrimary }}>{stat.label}</div>
                  <div style={{ ...styles.small }}>{stat.sublabel}</div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: 'clamp(3rem, 6vw, 5rem) 0', backgroundColor: COLORS.blueLight }}>
        <div style={styles.container}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', maxWidth: '36rem', margin: '0 auto' }}>
              <h2 style={{ ...styles.h2, marginBottom: '1rem' }}>
                Ready to Transform Your Sales Workflow?
              </h2>
              <p style={{ ...styles.body, marginBottom: '2rem' }}>
                Start your free 14-day trial today. No credit card required.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
                <button
                  onClick={handleGetStarted}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.875rem 1.75rem',
                    backgroundColor: COLORS.bgAccent,
                    color: COLORS.textOnAccent,
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                >
                  Start Free Trial
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={handlePricing}
                  style={{
                    padding: '0.875rem 1.75rem',
                    backgroundColor: COLORS.bgPanel,
                    color: COLORS.textPrimary,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                >
                  View Pricing
                </button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: 'clamp(2rem, 4vw, 3rem) 0', backgroundColor: COLORS.bgPanel, borderTop: `1px solid ${COLORS.borderLight}` }}>
        <div style={styles.container}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '1.5rem',
                height: '1.5rem',
                borderRadius: '0.25rem',
                backgroundColor: COLORS.bgAccent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Target size={12} color={COLORS.textOnAccent} />
              </div>
              <span style={{ fontWeight: 700, color: COLORS.textPrimary }}>Cenari</span>
            </div>

            <div style={{ display: 'flex', gap: '2rem', fontSize: '0.875rem' }}>
              {['Privacy', 'Terms', 'Support'].map((link, i) => (
                <a key={i} href="#" style={{ color: COLORS.textMuted, textDecoration: 'none' }}>{link}</a>
              ))}
            </div>

            <div style={{ fontSize: '0.875rem', color: COLORS.textMuted }}>
              © 2026 Cenari. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageNew;
